import {
  Flex,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useClipboard,
  useToast,
} from '@chakra-ui/react';
import { IconCircleInfo } from 'central-icons/IconCircleInfo';
import React, { forwardRef, useEffect, useState } from 'react';
import type { NodeApi } from 'react-arborist';

import { EntityInfoContent } from './EntityInfoContent';
import styles from './EntityInfoPopover.module.css';
import type { NodeData } from './Node';
import { TopCompaniesContent } from './TopCompaniesContent';
import type { TreeData } from './useTreeApi';

export type EntityInfoData = {
  ult_parent_name?: string;
  ult_parent_rcid?: number;
  hasUltimateParent?: boolean;
  primary_website?: string;
  primary_linkedin_url?: string;
  rics_k400?: number;
  city?: string;
  country?: string;
  industry?: string;
  rcid?: number;
  top_companies?: string[];
};

export type EntityInfoPopoverProps = {
  node: NodeApi<TreeData<EntityInfoData>>;
};

export const EntityInfoPopover = forwardRef<
  HTMLDivElement,
  EntityInfoPopoverProps
>(({ node }, ref) => {
  const data: NodeData<EntityInfoData> = node.data;
  const { entityInfo, name: nodeName } = data;
  const contentRef = React.useRef<HTMLDivElement>(null);
  const toast = useToast();
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { onCopy } = useClipboard(
    entityInfo?.ult_parent_rcid?.toString() ||
      entityInfo?.rcid?.toString() ||
      ''
  );

  React.useImperativeHandle(ref, () => contentRef.current as HTMLDivElement);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  if (!entityInfo) {
    return null;
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCopy();

    toast({
      title: 'RCID Copied',
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  };

  if (!entityInfo) {
    return null;
  }

  return (
    <Popover
      variant="tableTooltip"
      trigger="hover"
      placement="top"
      closeDelay={100}
      openDelay={500}
      arrowSize={14}
      isLazy
      offset={[0, 12]}
      closeOnBlur={false}
      gutter={10}
      onOpen={() => setIsPopoverOpen(true)}
      onClose={() => setIsPopoverOpen(false)}
    >
      <PopoverTrigger>
        <Flex justifyContent="center" alignItems="center">
          <Icon
            size={12}
            as={IconCircleInfo}
            className={styles.infoIcon}
            cursor="pointer"
            onClick={(e) => e.stopPropagation()}
            display={isPopoverOpen ? 'flex' : 'none'}
            _groupHover={{
              display: 'flex',
            }}
          />
        </Flex>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          ref={contentRef}
          border="none"
          borderRadius="md"
          p={3}
          width="350px"
        >
          <PopoverArrow />
          <PopoverBody p={0} boxShadow="none">
            {entityInfo.top_companies?.length ? (
              <TopCompaniesContent
                companies={entityInfo.top_companies}
                nodeName={nodeName}
              />
            ) : (
              <EntityInfoContent
                nodeName={nodeName}
                entityInfo={entityInfo}
                onCopy={handleCopy}
                isShiftPressed={isShiftPressed}
              />
            )}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
});
